import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { C, TABLE_HEAD_SX } from './tokens';
import CandidateRow from './CandidateRow';
import { BreakoutTableSkeletonRows } from './skeletons';

// Sticky-header table that renders the candidate list.
// Mobile (`isCompact`) hides secondary columns to keep the row scannable.
const BreakoutCandidatesTable = ({
  loading,
  candidates,
  isCompact,
  t,
  language,
  locale,
  onOpenDetail,
}) => {
  const visibleColumnCount = isCompact ? 5 : 13;
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: '#e5e7eb',
        '&::-webkit-scrollbar': { height: 8, width: 8 },
        '&::-webkit-scrollbar-thumb': { bgcolor: C.borderSoft, borderRadius: 4 },
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...TABLE_HEAD_SX, width: 42 }} align="center">#</TableCell>
            <TableCell sx={{ ...TABLE_HEAD_SX, width: isCompact ? 220 : 180 }}>
              {t('breakoutCandidates.table.ticker')}
            </TableCell>
            {isCompact ? null : (
              <TableCell sx={{ ...TABLE_HEAD_SX, width: 150 }}>
                {t('breakoutCandidates.table.sector')}
              </TableCell>
            )}
            <TableCell sx={{ ...TABLE_HEAD_SX, width: 72 }} align="right">
              {t('breakoutCandidates.table.score')}
            </TableCell>
            <TableCell sx={{ ...TABLE_HEAD_SX, width: 92 }}>
              {t('breakoutCandidates.table.signal')}
            </TableCell>
            <TableCell sx={{ ...TABLE_HEAD_SX, width: 64 }}>
              {t('breakoutCandidates.table.aiConfidence', 'AI')}
            </TableCell>
            <TableCell sx={{ ...TABLE_HEAD_SX, width: 92 }} align="right">
              {t('breakoutCandidates.table.price')}
            </TableCell>
            {isCompact ? null : (
              <>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 90 }} align="right">{t('breakoutCandidates.table.distR20')}</TableCell>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 74 }} align="right">{t('breakoutCandidates.table.ud')}</TableCell>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 72 }} align="right">{t('breakoutCandidates.table.accum')}</TableCell>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 96 }} align="right">{t('breakoutCandidates.table.liquidity')}</TableCell>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 86 }} align="right">{t('breakoutCandidates.table.strength')}</TableCell>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 62 }} align="right">RS</TableCell>
                <TableCell sx={{ ...TABLE_HEAD_SX, width: 88 }}>{t('breakoutCandidates.table.tier')}</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading && candidates.length === 0 ? (
            <BreakoutTableSkeletonRows isCompact={isCompact} />
          ) : null}

          {candidates.map((row, index) => (
            <CandidateRow
              key={`${row.ticker}-${row.score_date}`}
              row={row}
              index={index}
              onOpenDetail={onOpenDetail}
              t={t}
              language={language}
              locale={locale}
              isCompact={isCompact}
            />
          ))}

          {!loading && candidates.length === 0 ? (
            <TableRow>
              <TableCell colSpan={visibleColumnCount} sx={{ textAlign: 'center', py: 5, color: C.muted }}>
                {t('breakoutCandidates.table.empty')}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

BreakoutCandidatesTable.propTypes = {
  loading: PropTypes.bool,
  candidates: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  isCompact: PropTypes.bool,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
  locale: PropTypes.string,
  onOpenDetail: PropTypes.func.isRequired,
};

BreakoutCandidatesTable.defaultProps = {
  loading: false,
  isCompact: false,
  language: 'en',
  locale: 'en-US',
};

export default BreakoutCandidatesTable;
